import * as React from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { fetchApi, notifyError, notifyResponse } from 'gossamer-front-end';
import {
  AdminApplyRecurringEventEditRequest,
  AdminPreviewRecurringEventEditRequest,
  RecurringEventEditFields,
  RecurringEventEditPreview,
  RecurringEventEditScope,
} from 'cb-model';

interface EventRecurringEditModalProps {
  eventId: number;
  scope: RecurringEventEditScope;
  proposedFields: RecurringEventEditFields;
  submittedStartDate?: string;
  submittedEndDate?: string;
  show: boolean;
  onHide: () => void;
  onApplied: () => void;
}

const scopeLabel = (scope: RecurringEventEditScope) =>
  scope === 'this-and-future' ? 'this event and future active events' : 'the active future events in this series';

export const EventRecurringEditModal = (props: EventRecurringEditModalProps): React.JSX.Element => {
  const [includeExceptions, setIncludeExceptions] = React.useState(false);
  const [preview, setPreview] = React.useState<RecurringEventEditPreview>();
  const [previewing, setPreviewing] = React.useState(false);
  const [applying, setApplying] = React.useState(false);

  React.useEffect(() => {
    if (!props.show) return;
    setIncludeExceptions(false);
    setPreview(undefined);
  }, [props.show, props.scope, props.proposedFields]);

  const request = (): AdminPreviewRecurringEventEditRequest => ({
    eventId: props.eventId,
    scope: props.scope,
    includeExceptions,
    proposedFields: props.proposedFields,
    submittedStartDate: props.submittedStartDate,
    submittedEndDate: props.submittedEndDate,
  });

  const handlePreview = async () => {
    if (previewing || applying) return;
    setPreviewing(true);
    try {
      const response = await fetchApi('POST', '/main/recurring-event-edit-preview', request());
      const nextPreview = (response as { recurringEventEditPreview?: RecurringEventEditPreview })
        .recurringEventEditPreview;
      if (!nextPreview) throw new Error('The server did not return an edit preview');
      setPreview(nextPreview);
      notifyResponse(response);
    } catch (error) {
      notifyError(error, 'Could not preview the recurring event update');
    }
    setPreviewing(false);
  };

  const handleApply = async () => {
    if (!preview || applying) return;
    setApplying(true);
    try {
      const response = await fetchApi(
        'POST',
        '/main/recurring-event-edit-apply',
        request() as AdminApplyRecurringEventEditRequest,
      );
      notifyResponse(response);
      props.onApplied();
    } catch (error) {
      notifyError(error, 'Could not save the recurring event update');
    }
    setApplying(false);
  };

  return (
    <Modal show={props.show} onHide={props.onHide} centered aria-labelledby="recurring-event-edit-modal-title">
      <Modal.Header closeButton={!previewing && !applying}>
        <Modal.Title id="recurring-event-edit-modal-title">Confirm recurring event update</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This will save the submitted fields to <strong>{scopeLabel(props.scope)}</strong>. Tickets are never changed
          by this action.
        </p>
        <Alert variant="secondary">
          If the selected start time changes, every affected occurrence moves by the same amount. Their own durations
          are retained unless you changed the selected event&apos;s duration, which is then used for every affected
          event. Each move uses the normal reschedule safeguards and attendee notifications.
        </Alert>
        {props.scope === 'entire-series' && (
          <Alert variant="info">
            For safety, historical occurrences are not changed. This action affects active occurrences from now onward.
          </Alert>
        )}
        <Form.Check
          type="checkbox"
          id="recurring-event-edit-include-exceptions"
          label="Also include individually edited, rescheduled, cancelled, or manually added exceptions"
          checked={includeExceptions}
          disabled={previewing || applying}
          onChange={(event) => {
            setIncludeExceptions(event.currentTarget.checked);
            setPreview(undefined);
          }}
        />
        {preview && (
          <div className="mt-3">
            <strong>Preview</strong>
            <ul className="mb-0 mt-2">
              <li>{preview.affected_event_count} event(s) will be updated</li>
              <li>{preview.rescheduling_event_count} event(s) will be rescheduled</li>
              <li>{preview.excluded_event_count} exception(s) excluded</li>
              <li>{preview.active_booking_count} active booking(s) on affected events</li>
              <li>{preview.payment_count} payment record(s) on affected events</li>
            </ul>
            {preview.paid_ticket_event_count > 0 && (
              <Alert variant="warning" className="mt-3 mb-0">
                {preview.paid_ticket_event_count} affected event(s) have paid tickets. The normal reschedule restriction
                prevents this time change; adjust the scope or save non-time fields only.
              </Alert>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={props.onHide} disabled={previewing || applying}>
          Cancel
        </Button>
        <Button variant="secondary" onClick={handlePreview} disabled={previewing || applying}>
          {previewing ? 'Previewing…' : 'Preview changes'}
        </Button>
        <Button
          variant="primary"
          onClick={handleApply}
          disabled={!preview || preview.paid_ticket_event_count > 0 || previewing || applying}
        >
          {applying ? 'Saving…' : 'Apply previewed changes'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
